import React, { FC, useMemo, useRef, MouseEvent } from 'react';

import { useInkBar, useMoreDropdown, useResizeTabBar } from './hooks';

import { MoreDropdown } from './MoreDropdown';

import { InkBar, Size, StyledTab, TabsContent, TabsWrapper } from './styled';

import { TabBarProps } from './types';

const TabBar: FC<TabBarProps> = ({
  activeKey,
  size = Size.m,
  moreIcon,
  onTabClick,
  panes,
  dropDownTrigger,
}) => {
  const refWrapper = useRef() as React.RefObject<HTMLDivElement>;
  const wrapperWidth = useResizeTabBar(refWrapper);
  const dropdownPanes = useMoreDropdown({ panes, refWrapper, wrapperWidth });
  const inkBarStyles = useInkBar({ refWrapper, dropdownPanes, activeKey, wrapperWidth });

  const tabItems = useMemo(
    () =>
      panes.map(({ key, props: { tab, disabled } }) => {
        const onClick = disabled
          ? undefined
          : (event: MouseEvent<HTMLDivElement>) => onTabClick(key, event);

        return (
          <StyledTab
            key={key}
            data-tab-active={activeKey === key}
            onClick={onClick}
            isEmpty={dropdownPanes.some(({ key: paneKey }) => paneKey === key)}
            active={activeKey === key}
            size={size}
            disabled={disabled}
            data-name='tab'
          >
            {tab}
          </StyledTab>
        );
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeKey, panes, dropdownPanes],
  );

  return (
    <TabsWrapper ref={refWrapper}>
      <TabsContent>
        {tabItems}
        <MoreDropdown
          dropDownTrigger={dropDownTrigger}
          size={size}
          onTabClick={onTabClick}
          items={dropdownPanes}
          activeKey={activeKey}
          moreIcon={moreIcon}
        />
        <InkBar {...inkBarStyles} />
      </TabsContent>
    </TabsWrapper>
  );
};

export default TabBar;
